import React, { useMemo } from "react";
import { T } from "services/lang/LanguageService";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { GaugeComponent, PointerProps, SubArc, Tick } from "react-gauge-component";
import { DialIndicatorProps, ValueType } from "./DialIndicatorTypes";

const DEFAULT_DARK_COLOR = "#3c4962";
const DEFUALT_RED_COLOR = "#D95648";

const pointerConfig: PointerProps = {
  type: "arrow",
  color: DEFAULT_DARK_COLOR,
  length: 1,
  width: 5,
};

const valueLabelStyle = {
  fontSize: 20,
  fontWeight: "bold",
textShadow: "none",
};

export default function DialIndicator(props: DialIndicatorProps) {
  const identifiers = useMemo(() => {
    const ids = [props.valueDataset];
    if (typeof props.minValue === "string") ids.push(props.minValue);
    if (typeof props.maxValue === "string") ids.push(props.maxValue);
    return ids;
  }, [props.valueDataset, props.minValue, props.maxValue]);

  const { data } = useGetRecentData(identifiers, {
    options: {
      refetchInterval: (props.updateFrequency ?? 5) * 1000,
    },
  });

  const minValue = useMemo(() => {
    if (props.minValueType === ValueType.DATASET) {
      return data?.find(d => d.identifier === props.minValue)?.data[0]?.value ?? 0;
    }
    return (props.minValue as number) ?? 0;
  }, [props.minValue, props.minValueType, data]);

  const maxValue = useMemo(() => {
    if (props.maxValueType === ValueType.DATASET) {
      return data?.find(d => d.identifier === props.maxValue)?.data[0]?.value ?? 0;
    }
    return (props.maxValue as number) ?? 0;
  }, [props.maxValue, props.maxValueType, data]);

  const getSubArcs = () => {
    const min = minValue;
    const max = maxValue;

    const zones: SubArc[] =
      props.zones
        ?.map(zone => ({
          limit: zone.maxValue,
          color: zone.color,
          showTick: true,
        }))
        .filter(subArc => subArc.limit >= min && subArc.limit <= max) ?? [];

    zones.sort((a, b) => (a.limit ?? 0) - (b.limit ?? 0));
    zones.push({ color: props.lastZoneColor ?? DEFUALT_RED_COLOR });
    return zones;
  };

  const ticks: Tick[] = props.ticks?.filter(
    tick => tick.value >= minValue && tick.value <= maxValue
  );

  const unit = data?.[0]?.unit ?? "";

  if (!data) {
    return <div className="bg-red-100 p-2 border-round-md">{T("error.no-available-data")}</div>;
  }

  if (minValue >= maxValue) {
    return (
      <div className="bg-red-100 p-2 border-round-md">
        {T("error.min-value-must-be-less-than-max-value")}
      </div>
    );
  }

  return (
    <div>
      {data && (
        <GaugeComponent
          className="gauge-component-style"
          type="semicircle"
          arc={{
            width: 0.2,
            padding: 0.01,
            cornerRadius: 5,
            subArcs: getSubArcs(),
          }}
          pointer={pointerConfig}
          labels={{
            valueLabel: {
              formatTextValue: value => `${value}${unit}`,
              style: valueLabelStyle,
              hide: false,
              matchColorWithArc: true,
            },
            tickLabels: {
              type: "outer",
              defaultTickValueConfig: {
                hide: props.hideTicks,
                formatTextValue: (value: any) => `${value}${unit}`,
                style: {
                  fontSize: props.ticksFontSize ?? 15,
                  fill: DEFAULT_DARK_COLOR,
                },
              },
              ticks,
            },
          }}
          value={data[0].data[0].value}
          minValue={minValue}
          maxValue={maxValue}
        />
      )}
    </div>
  );
}
